import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { summarizeExtras } from "@/lib/extras-signals";

const InputSchema = z.object({
  session_id: z.string().max(64).optional().nullable(),
  timeframe_start: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().nullable(),
  timeframe_end: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().nullable(),
  timeframe_label: z.string().max(32).optional().nullable(),
  budget_level: z.string().max(64).optional().nullable(),
  budget_max_dollars: z.number().int().min(0).max(100000).optional().nullable(),
  weather_exposure: z.number().int().min(-2).max(2).optional().nullable(),
  weather_preference: z.enum(["indoor", "either", "outdoor"]).optional().nullable(),
  duration_preference: z.string().max(16).optional().nullable(),
  origin_zip: z.string().max(16).optional().nullable(),
  max_drive_minutes: z.number().int().min(0).max(600).optional().nullable(),
  extras_text: z.string().max(500).optional().nullable(),
});

function sanitizeExtras(text: string): string {
  return text
    // strip control chars except newline/tab
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, "")
    // collapse runs of whitespace; preserve at most single newline
    .replace(/[ \t]+/g, " ")
    .replace(/\n{2,}/g, "\n")
    .trim()
    .slice(0, 500);
}

export const logWizardResponse = createServerFn({ method: "POST" })
  .inputValidator((data) => InputSchema.parse(data))
  .handler(async ({ data }) => {
    const extrasRaw = (data.extras_text ?? "").toString();
    const newlineCount = (extrasRaw.match(/\n/g) || []).length;
    if (newlineCount > 5) {
      return { ok: false, error: "Too many line breaks in extras." } as const;
    }
    const extrasClean = sanitizeExtras(extrasRaw);
    const summary = extrasClean ? summarizeExtras(extrasClean) : null;
    const has_local_passes = !!summary && summary.hasMembership.length > 0;

    const row = {
      session_id: data.session_id ?? null,
      timeframe_start: data.timeframe_start ?? null,
      timeframe_end: data.timeframe_end ?? null,
      timeframe_label: data.timeframe_label ?? null,
      budget_level: data.budget_level ?? null,
      budget_max_dollars: data.budget_max_dollars ?? null,
      weather_exposure: data.weather_exposure ?? null,
      weather_preference: data.weather_preference ?? null,
      duration_preference: data.duration_preference ?? null,
      origin_zip: data.origin_zip ?? null,
      max_drive_minutes: data.max_drive_minutes ?? null,
      extras_length: extrasClean.length,
      extras_signals: summary as unknown as Record<string, unknown> | null,
      has_local_passes,
      group_size: null,
      indoor_outdoor: null,
    };

    const { error } = await (supabaseAdmin as any).from("wizard_responses").insert(row);
    if (error) {
      console.error("[logWizardResponse] insert failed:", error.message);
      return { ok: false, error: "Could not log response." } as const;
    }
    return { ok: true } as const;
  });